/**
 * Auth / account hooks (§5.2 "Auth / account"). Owner: frontend-foundation.
 *
 * Session model: `useMe()` resolves to the signed-in user or `null` (not signed in); a
 * 401 from any other request sets it to `null` and `RequireAuth` sends the user to
 * `/login?next=…` (wired in `src/main.tsx`).
 */
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, isApiError } from '../client';
import { qk } from '../keys';
import type {
  AcceptInviteRequest,
  ApiToken,
  ApiTokenCreate,
  ApiTokenCreated,
  ChangePasswordRequest,
  ChangePasswordResult,
  InviteInfo,
  LoginRequest,
  Me,
} from '../types';

/** Minimum password length enforced by the API (§5.2). */
export const MIN_PASSWORD_LENGTH = 10;

export const meQuery = () =>
  queryOptions({
    queryKey: qk.auth.me(),
    queryFn: async ({ signal }): Promise<Me | null> => {
      try {
        return await api.get<Me>('/api/auth/me', { signal, redirectOn401: false });
      } catch (error) {
        if (isApiError(error) && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });

/** The signed-in user (`null` = signed out, `undefined` while loading). */
export function useMe() {
  return useQuery(meQuery());
}

/**
 * The signed-in user inside authenticated routes (under `RequireAuth`, which guarantees
 * it is loaded). Throws when used elsewhere.
 */
export function useCurrentUser(): Me {
  const { data } = useMe();
  if (!data) throw new Error('useCurrentUser() used outside an authenticated route');
  return data;
}

/** Drops every cached query except the session and stores `me` (login / invite / 401). */
export function resetSession(queryClient: QueryClient, me: Me | null): void {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
  queryClient.removeQueries({ queryKey: qk.auth.tokens() });
  queryClient.setQueryData(qk.auth.me(), me);
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => api.post<Me>('/api/auth/login', body, { redirectOn401: false }),
    onSuccess: (me) => resetSession(queryClient, me),
    meta: { errorToast: false },
  });
}

/** Signs out and reloads on `/login` (a full reload also resets all in-memory stores). */
export function useLogout() {
  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout', undefined, { redirectOn401: false }),
    onSettled: () => window.location.assign('/login'),
    meta: { errorToast: false },
  });
}

export const inviteQuery = (token: string) =>
  queryOptions({
    queryKey: qk.auth.invite(token),
    // POST with the token in the body: it never appears in a URL / access log
    queryFn: ({ signal }) =>
      api.post<InviteInfo>('/api/auth/invite-info', { token }, { signal, redirectOn401: false }),
    staleTime: Number.POSITIVE_INFINITY,
  });

export function useInviteInfo(token: string) {
  return useQuery({ ...inviteQuery(token), enabled: token.length > 0 });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AcceptInviteRequest) =>
      api.post<Me>('/api/auth/accept-invite', body, { redirectOn401: false }),
    onSuccess: (me) => resetSession(queryClient, me),
    meta: { errorToast: false },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) =>
      api.post<ChangePasswordResult>('/api/auth/change-password', body),
    meta: { errorToast: false },
  });
}

export const apiTokensQuery = () =>
  queryOptions({
    queryKey: qk.auth.tokens(),
    queryFn: ({ signal }) => api.get<ApiToken[]>('/api/auth/tokens', { signal }),
  });

export function useApiTokens() {
  return useQuery(apiTokensQuery());
}

/** Creates a personal API token; the secret is only in this response (`token`). */
export function useCreateApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiTokenCreate) => api.post<ApiTokenCreated>('/api/auth/tokens', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.auth.tokens() }),
    meta: { errorToast: false },
  });
}

export function useDeleteApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: number) => api.delete(`/api/auth/tokens/${tokenId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.auth.tokens() }),
    meta: { errorTitle: 'Could not revoke the token' },
  });
}
